import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Divider,
} from "@mui/material";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";

const coreValues = [
  {
    id: 1,
    title: "Innovation",
    description:
      "Building future-ready telecom platforms that push the boundaries of what's possible in communication technology. Our R&D team constantly explores emerging technologies and trends to deliver cutting-edge solutions.",
    icon: <LightbulbOutlinedIcon fontSize="large" />,
    color: "#2196f3",
    examples: [
      "Developing AI-powered communication systems",
      "Creating high-throughput messaging platforms",
      "Pioneering next-gen voice technology solutions",
    ],
  },
  {
    id: 2,
    title: "Integrity",
    description:
      "Operating with trust and compliance at every level, ensuring our solutions meet the highest standards of security and reliability. We maintain transparency with our clients and partners, building lasting relationships based on mutual respect.",
    icon: <SecurityOutlinedIcon fontSize="large" />,
    color: "#673ab7",
    examples: [
      "End-to-end regulatory compliance",
      "Robust security protocols and data protection",
      "Transparent business practices and relationships",
    ],
  },
  {
    id: 3,
    title: "Customer Success",
    description:
      "Enabling excellence through reliable services that help our clients achieve their goals and exceed their expectations. We measure our success by the success of our customers and their satisfaction with our products and services.",
    icon: <EmojiEventsOutlinedIcon fontSize="large" />,
    color: "#e91e63",
    examples: [
      "Dedicated customer success teams",
      "Customized solutions for unique challenges",
      "Regular product updates based on feedback",
    ],
  },
];

const CoreValues = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        py: 10,
        backgroundColor:
          theme.palette.mode === "light"
            ? alpha(theme.palette.primary.main, 0.03)
            : alpha(theme.palette.primary.dark, 0.2),
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Abstract background shapes */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.5,
          zIndex: 0,
          overflow: "hidden",
          "& > div": {
            position: "absolute",
            borderRadius: "50%",
          },
        }}
      >
        <Box
          sx={{
            width: "40vw",
            height: "40vw",
            left: "-20vw",
            top: "-10vw",
            background: `radial-gradient(circle, ${alpha(
              coreValues[0].color,
              0.2
            )} 0%, transparent 70%)`,
          }}
        />
        <Box
          sx={{
            width: "30vw",
            height: "30vw",
            right: "-10vw",
            bottom: "-10vw",
            background: `radial-gradient(circle, ${alpha(
              coreValues[1].color,
              0.2
            )} 0%, transparent 70%)`,
          }}
        />
        <Box
          sx={{
            width: "20vw",
            height: "20vw",
            right: "30vw",
            top: "20vh",
            background: `radial-gradient(circle, ${alpha(
              coreValues[2].color,
              0.15
            )} 0%, transparent 70%)`,
          }}
        />
      </Box>

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ mb: 8, textAlign: "center" }}>
          <Typography
            variant="h2"
            component="h2"
            color="text.primary"
            sx={{
              mb: 3,
              fontWeight: 700,
              position: "relative",
              display: "inline-block",
            }}
          >
            Our Core Values
            <Box
              sx={{
                position: "absolute",
                height: "4px",
                width: "40%",
                bottom: "-10px",
                left: "30%",
                backgroundColor: "primary.main",
                borderRadius: "4px",
              }}
            />
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 700, mx: "auto", fontWeight: 400 }}
          >
            The principles that guide everything we do at ProtoDiag Solutions
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {coreValues.map((value, index) => (
            <Grid item xs={12} md={4} key={value.id}>
              <Card
                elevation={4}
                sx={{
                  height: "100%",
                  borderRadius: 3,
                  transition: "transform 0.3s, box-shadow 0.3s",
                  overflow: "visible",
                  position: "relative",
                  "&:hover": {
                    transform: "translateY(-12px)",
                    boxShadow: theme.shadows[15],
                    "& .value-icon-container": {
                      transform: "scale(1.1)",
                    },
                  },
                }}
              >
                <Box
                  className="value-icon-container"
                  sx={{
                    position: "absolute",
                    top: "-30px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: value.color,
                    color: "white",
                    boxShadow: `0 10px 20px ${alpha(value.color, 0.4)}`,
                    transition: "transform 0.3s",
                    border: `4px solid ${theme.palette.background.paper}`,
                    zIndex: 2,
                  }}
                >
                  {value.icon}
                </Box>

                <CardContent
                  sx={{
                    p: 4,
                    pt: 6,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    mt: 2,
                  }}
                >
                  <Typography
                    variant="h4"
                    component="h3"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      textAlign: "center",
                      color: value.color,
                    }}
                  >
                    {value.title}
                  </Typography>

                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {value.description}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Typography
                    variant="subtitle2"
                    color="text.primary"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    How we demonstrate this:
                  </Typography>

                  <Box component="ul" sx={{ pl: 2, m: 0, mt: 1 }}>
                    {value.examples.map((example, i) => (
                      <Typography
                        component="li"
                        variant="body2"
                        color="text.secondary"
                        key={i}
                        sx={{ mb: 1 }}
                      >
                        {example}
                      </Typography>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default CoreValues;
